import { UserProfile } from '../../utils/interfaces';
import {
  Form,
  FormElement,
  FormElementChange,
  Step,
  ValidateOptions,
} from '@tradein/api';

const formElementValuesKey = 'tradein-formelementvalues';

function checkIfRequiredFieldPresent(
  currentFormElement: FormElement,
  allFormElements: FormElement[],
): boolean {
  const requiredField = allFormElements.find(
    (cFE) =>
      cFE.key === currentFormElement?.options?.disabledOptions?.requiredField,
  );

  return (
    requiredField?.field.selectedValue !== null &&
    requiredField?.options?.valid === true
  );
}

export function checkDisabledState(
  currentFormElement: FormElement,
  allFormElements: FormElement[],
): boolean {
  if (
    currentFormElement.options &&
    currentFormElement.options.disabledOptions &&
    currentFormElement.options.disabledOptions.requiredField &&
    !checkIfRequiredFieldPresent(currentFormElement, allFormElements)
  ) {
    return true;
  } else {
    return false;
  }
}

export function checkDisplayState(
  currentFormElement: FormElement,
  allFormElements: FormElement[],
): boolean {
  if (currentFormElement.options && currentFormElement.options.displayOptions) {
    for (const displayOption of currentFormElement.options.displayOptions) {
      const formElementValue = allFormElements.find(
        (cFE) => cFE.key === displayOption.formElementKey,
      )?.field.selectedValue;

      if (!formElementValue) {
        return false;
      }

      if (
        !displayOption.value.includes(formElementValue) &&
        !(
          displayOption.value.includes('*') &&
          allFormElements.find(
            (cFE) => cFE.key === displayOption.formElementKey,
          )?.options.valid
        )
      ) {
        return false;
      }
    }
  }

  return true;
}

export function checkValidState(currentFormElement: FormElement): {
  valid: boolean;
  errorMessages: string[];
} {
  const messages: string[] = [];

  if (
    currentFormElement.options &&
    currentFormElement.options.validateOptions &&
    currentFormElement.options.display !== false
  ) {
    for (const validateOption of currentFormElement.options.validateOptions) {
      getErrorMessage(currentFormElement, validateOption, messages);
    }
  }

  return {
    valid: messages.length === 0,
    errorMessages: messages,
  };
}

function getErrorMessage(
  currentFormElement: FormElement,
  validateOption: ValidateOptions,
  messages: string[],
) {
  if (validateOption.message) {
    const value = currentFormElement.field.selectedValue || '';
    if (!messages.includes(validateOption.message)) {
      if (validateOption.required && !value) {
        messages.push(validateOption.message);
      }
      if (value) {
        !checkLengthValid(value, validateOption) &&
          messages.push(validateOption.message);
        !checkPatternValid(value, validateOption) &&
          messages.push(validateOption.message);
        !checkMonthValid(currentFormElement, value, validateOption) &&
          messages.push(validateOption.message);
        !checkNumberValid(currentFormElement, value, validateOption) &&
          messages.push(validateOption.message);
      }
    }
  }
}

export function checkLengthValid(
  value: string,
  validateOption: ValidateOptions,
): boolean {
  if (validateOption.minLength && value.length < validateOption.minLength) {
    return false;
  }

  if (validateOption.maxLength && value.length > validateOption.maxLength) {
    return false;
  }
  return true;
}

export function checkPatternValid(
  value: string,
  validateOption: ValidateOptions,
): boolean {
  if (validateOption.pattern) {
    const regPattern = validateOption.pattern || '';
    const regExp = new RegExp(regPattern);
    const str = value.toString() || '';
    return !str.match(regExp) ? false : true;
  }
  return true;
}

export function checkMonthValid(
  currentFormElement: FormElement,
  value: string,
  validateOption: ValidateOptions,
): boolean {
  if (currentFormElement.type === 'Month') {
    const split = value && value.split(/[./-]/);
    if (value.length === 7 && split.length === 2) {
      const valueDate =
        split[0].length === 4
          ? new Date(`${split[0]}-${split[1]}`).getTime()
          : new Date(`${split[1]}-${split[0]}`).getTime();

      if (
        validateOption.min &&
        valueDate < new Date(validateOption.min).getTime()
      ) {
        return false;
      }

      if (
        validateOption.max &&
        valueDate > new Date(validateOption.max).getTime()
      ) {
        return false;
      }
    }
  }

  return true;
}

export function checkNumberValid(
  currentFormElement: FormElement,
  value: string,
  validateOption: ValidateOptions,
): boolean {
  if (currentFormElement.type === 'Number') {
    if (
      (validateOption.min && parseInt(value) < parseInt(validateOption.min)) ||
      (validateOption.max && parseInt(value) > parseInt(validateOption.max))
    ) {
      return false;
    }
  }

  return true;
}

export function checkStepFormValid(step: Step): boolean {
  return step.form ? checkFormValid(step.form) : false;
}

export function checkFormValid(form: Form): boolean {
  for (const section of form.sections) {
    for (const element of section.elements) {
      if (
        !element.options.valid &&
        element.options.display &&
        element.options.validateOptions?.find((option) => option)?.required ===
          true
      ) {
        return false;
      }
    }
  }

  return true;
}

export const getFormChanges = (
  form: Form,
  element: FormElement,
  updatedElement: FormElement,
): void => {
  if (element.options.updateCall) {
    const updatedSectionIndex =
      form.sections.findIndex((section) =>
        section.elements.find((element) => element.key === updatedElement.key),
      ) || 0;
    const updatedElementIndex =
      form.sections[updatedSectionIndex].elements.findIndex(
        (element) => element.key === updatedElement.key,
      ) || 0;

    const change: FormElementChange = {
      formElementKey: element.key,
      originalValue: element.field.selectedValue || undefined,
      selectedValue: updatedElement.field.selectedValue || undefined,
    };

    for (
      let i = updatedElementIndex + 1;
      i < form.sections[updatedSectionIndex].elements.length;
      i++
    ) {
      form.sections[updatedSectionIndex].elements[i].field.selectedValue = '';
    }

    if (!change.originalValue && !change.selectedValue) {
      return;
    }

    form.changes = [change];
  }
};

export const checkForm = (form: Form): void => {
  if (form && form.sections) {
    for (const section of form.sections) {
      for (const element of section.elements) {
        checkFormElement(element, form);
      }
    }
  }
};

export function checkFormElement(
  currentFormElement: FormElement,
  form: Form,
): FormElement {
  const allFormElements: FormElement[] = [];

  for (const section of form.sections) {
    allFormElements.push(...section.elements);
  }

  currentFormElement.options.disabled = checkDisabledState(
    currentFormElement,
    allFormElements,
  );
  currentFormElement.options.display = checkDisplayState(
    currentFormElement,
    allFormElements,
  );

  return currentFormElement;
}

interface FormElementValues {
  selectedValue: string;
  key: string;
  type: string;
}

export function saveFormElementValues(
  form: Form,
  allowedKeys: string[] = [],
): void {
  for (const section of form.sections) {
    for (const element of section.elements) {
      const formElementValues: FormElementValues = {
        selectedValue: element.field.selectedValue,
        key: element.key,
        type: element.type,
      };

      if (allowedKeys.length === 0 || allowedKeys.indexOf(element.key) !== -1) {
        saveFormElementValuesToLocalStorage(formElementValues);
      }
    }
  }
}

export function saveFormElementValuesToLocalStorage(
  formElementValues: FormElementValues,
): void {
  const savedFormElementValues =
    localStorage.getItem(formElementValuesKey) || null;
  const parsedFormElementValues: FormElementValues[] = savedFormElementValues
    ? (JSON.parse(savedFormElementValues) as FormElementValues[])
    : [];
  const savedFormElementIndex = parsedFormElementValues.findIndex(
    (value) => value.key === formElementValues.key,
  );
  if (savedFormElementIndex !== -1) {
    parsedFormElementValues[savedFormElementIndex] = formElementValues;
  } else {
    parsedFormElementValues.push(formElementValues);
  }
  localStorage.setItem(
    formElementValuesKey,
    JSON.stringify(parsedFormElementValues),
  );
}

export function getFormElementValueFormLocalStorage(
  formElement: FormElement,
): string {
  const savedFormElementValues =
    localStorage.getItem(formElementValuesKey) || null;
  const parsedFormElementValues: FormElementValues[] = savedFormElementValues
    ? (JSON.parse(savedFormElementValues) as FormElementValues[])
    : [];
  const savedFormElementValue = parsedFormElementValues.find(
    (value) => value.key === formElement.key && value.type === formElement.type,
  );
  return savedFormElementValue ? savedFormElementValue.selectedValue : '';
}

export function saveMMEValuesToLocalStorage(
  userProfile: UserProfile | null,
): void {
  if (userProfile) {
    const firstname: FormElementValues = {
      key: 'firstname',
      selectedValue:
        userProfile.firstName && userProfile.firstName !== 'undefined'
          ? userProfile.firstName
          : '',
      type: 'Text',
    };

    const lastname: FormElementValues = {
      key: 'lastname',
      selectedValue:
        userProfile.lastName1 && userProfile.lastName1 !== 'undefined'
          ? userProfile.lastName1
          : '',
      type: 'Text',
    };

    const email: FormElementValues = {
      key: 'email',
      selectedValue:
        userProfile.email && userProfile.email !== 'undefined'
          ? userProfile.email
          : '',
      type: 'Email',
    };

    const phone: FormElementValues = {
      key: 'phone',
      selectedValue:
        userProfile.mobilePhoneNumber &&
        userProfile.mobilePhoneNumber !== 'undefined'
          ? userProfile.mobilePhoneNumber
          : '',
      type: 'Tel',
    };

    saveFormElementValuesToLocalStorage(firstname);
    saveFormElementValuesToLocalStorage(lastname);
    saveFormElementValuesToLocalStorage(email);
    saveFormElementValuesToLocalStorage(phone);
  }
}

export function getFormElementByVariant(form: Form, variant: string): any {
  if (form && form.sections) {
    for (const section of form.sections) {
      const formElement = section.elements.find(
        (element) => element.variant === variant,
      );

      return formElement;
    }
  }
}
